import Issue from "../../../classes/Issue";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteField,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { forEach, isEmpty, pick } from "lodash";

/**
 * Whether an order-required issue should adjust the item's `quantityReady`.
 *
 * QC-originated issues carry a `packingSlipID`, and the QC page already
 * excludes their units from the packing slip's `ready` count
 * (`ready = min(ready, included - undeliverable)` in QualityControl.vue), so
 * `quantityReady` never counted them — adjusting again double-counts the
 * shortage (sale 01000031A line 1 dropped to `quantityReady = 2` when QC had
 * passed 3). Ad-hoc issues raised AFTER QC (no `packingSlipID`) act on units
 * that were already counted ready, so they must adjust. Mirrors the backend
 * `updateCriticalDamage` `!packingSlipID` guard in functions/issues.js.
 *
 * @param {object} issueData - The issue's `data` object.
 * @returns {boolean}
 */
export function issueAffectsQuantityReady(issueData) {
  return Boolean(issueData && issueData.itemID && !issueData.packingSlipID);
}

/**
 * Whether an issue currently HOLDS its units out of the item's `quantityReady`.
 *
 * An ad-hoc order-required issue (`issueAffectsQuantityReady`) takes its
 * `quantity` out of the item's ready count when it is filed: those units are
 * not deliverable or installable until the replacement arrives. The hold
 * lasts exactly as long as the issue is OPEN. Closing it — by hand from the
 * issue dialog, or through the order dialog's "Resolve sale issues" toggle — hands
 * the units back; re-opening it takes them again.
 *
 * The hold used to outlive the issue: it was only released by the
 * replacement's own QC (`computeQcReadyUpdate` credits `quantity -
 * quantityReady`), by deleting the issue, or by un-ticking "requires a new
 * order". A reorder that never crosses the dock — touch-up paint for a unit
 * that was already installed — is never QC'd, so sale 04000024A sat at
 * "production" with line 2 reading 0 ready / 1 delivered / 1 installed and
 * its reorder acks never received, long after the issue read "Resolved".
 *
 * @param {?object} issueData - The issue's `data` object (null for "none").
 * @returns {boolean}
 */
export function issueHoldsUnits(issueData) {
  return Boolean(
    issueData &&
      issueData.orderRequired &&
      !issueData.isResolved &&
      issueAffectsQuantityReady(issueData)
  );
}

/**
 * Per-item `quantityReady` deltas for an issue transition. Units held by the
 * OLD state are handed back (+), units held by the NEW state are taken (−);
 * `null` on either side is a create or a delete. Keyed by itemID so an edit
 * that moves the issue to another item releases one and takes from the other.
 *
 * @param {?object} before - Issue data before the write (null on create).
 * @param {?object} after - Issue data after the write (null on delete).
 * @returns {Object<string,number>} itemID → net delta (zero entries omitted).
 */
export function issueHoldDeltas(before, after) {
  const deltas = {};
  if (issueHoldsUnits(before)) {
    deltas[before.itemID] =
      (deltas[before.itemID] || 0) + (Number(before.quantity) || 0);
  }
  if (issueHoldsUnits(after)) {
    deltas[after.itemID] =
      (deltas[after.itemID] || 0) - (Number(after.quantity) || 0);
  }
  Object.keys(deltas).forEach((itemID) => {
    if (!deltas[itemID]) delete deltas[itemID];
  });
  return deltas;
}

/**
 * Move an item's `quantityReady` by `delta` (clamped to `[0, quantity]`) and
 * roll the change into the sale/labor counters. Resolves to the item (or null
 * when there is nothing to adjust) so callers can read its vendor.
 */
async function adjustQuantityReady(
  dispatch,
  state,
  { projectID, saleID, itemID, delta }
) {
  if (!delta) return null;
  const item = await dispatch("GET_SALE_ITEM", { projectID, saleID, itemID });
  if (!item) return null;

  const oldQuantityReady = item.data.quantityReady || 0;
  const newQuantityReady = Math.max(
    0,
    Math.min(item.data.quantity, oldQuantityReady + delta)
  );
  if (newQuantityReady === oldQuantityReady) return item;

  await updateDoc(doc(db, item.path), {
    quantityReady: newQuantityReady,
    modified: new Date(),
    modifiedBy: state.user.id,
  });

  await dispatch("UPDATE_SALE_ITEM_STATUS", {
    projectID,
    saleID,
    item,
    oldDelivered: item.data.quantityDelivered || 0,
    oldInstalled: item.data.quantityInstalled || 0,
    oldQuantity: item.data.quantity || 0,
    oldQuantityReady,
    oldQuantityScheduled: item.data.quantityScheduled || 0,
    newDelivered: item.data.quantityDelivered || 0,
    newInstalled: item.data.quantityInstalled || 0,
    newQuantity: item.data.quantity || 0,
    newQuantityReady,
    newQuantityScheduled: item.data.quantityScheduled || 0,
  });
  return item;
}

/**
 * Apply the hold transition between two issue states (see `issueHoldDeltas`),
 * then — for every vendor that got units BACK — re-evaluate its receipt. A
 * replacement that was never QC'd leaves its reorder acks un-received and the
 * sale at "production"; once the released units make the vendor's items whole,
 * `RAISE_VENDOR_RECEIPT` marks its orders and acks received. Taking units never
 * lowers a receipt flag, so only releases re-evaluate.
 */
async function applyIssueHoldTransition(
  dispatch,
  state,
  { projectID, saleID, before, after }
) {
  const deltas = issueHoldDeltas(before, after);
  const releasedVendors = new Set();
  for (const [itemID, delta] of Object.entries(deltas)) {
    const item = await adjustQuantityReady(dispatch, state, {
      projectID,
      saleID,
      itemID,
      delta,
    });
    if (delta > 0) {
      const vendor = (item && item.data.brand) || (before && before.vendor);
      if (vendor) releasedVendors.add(vendor);
    }
  }
  for (const vendor of releasedVendors) {
    await dispatch("RAISE_VENDOR_RECEIPT", { projectID, saleID, vendor });
  }
}

export const GET_ISSUE = async (
  { dispatch },
  { projectID, saleID, issueID }
) => {
  return dispatch("GET_DOC", {
    converter: Issue,
    path: Issue.getPath(projectID, saleID, issueID),
    stateKey: "issues",
    id: issueID,
  });
};

export const GET_ISSUES = (
  { dispatch },
  { projectID, saleID, conditions, limitCount, pagination }
) => {
  const path = projectID && saleID ? Issue.getPath(projectID, saleID) : null;
  return dispatch("GET_COLLECTION", {
    converter: Issue,
    stateKey: "issues",
    colGroup: !path ? "issues" : null,
    path,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_ISSUE = async (
  { dispatch },
  { projectID, saleID, issueID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Issue,
    path: Issue.getPath(projectID, saleID, issueID),
    stateKey: "issues",
    id: issueID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_ISSUES = (
  { dispatch },
  { projectID, saleID, conditions, listenerPath, error, next, unlisten }
) => {
  const path = projectID && saleID ? Issue.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Issue,
    stateKey: "issues",
    colGroup: !path ? "issues" : null,
    listenerPath,
    path,
    conditions,
    next,
    error,
    unlisten,
  });
};

export const SAVE_ISSUE = async (
  { dispatch, state },
  { projectID, saleID, issue }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: issue.data,
      fieldPath: "media",
      folderPath: issue.path,
      existingData,
    });
  };

  const updateSale = async (changes = {}) => {
    return dispatch("UPDATE_SALE_STATUS", {
      batch,
      projectID,
      saleID,
      changes: {
        ...changes,
        [`status.issues.${issue.id}.itemID`]: issue.itemID,
        [`status.issues.${issue.id}.isCritical`]: issue.isCritical,
        [`status.issues.${issue.id}.isResolved`]: issue.isResolved,
      },
    });
  };

  if (!issue.id) {
    // New Issue
    issue.data.created = new Date();
    issue.data.createdBy = state.user.id;

    const ref = doc(collection(db, Issue.getPath(projectID, saleID)));
    issue.id = ref.id;

    await saveDocuments();
    batch.set(ref.withConverter(Issue), issue);
    await updateSale({
      [`status.issues.${issue.id}`]: {
        orderID: issue.orderID,
        packingSlipID: issue.packingSlipID,
      },
    });
    await batch.commit();

    // A new open order-required issue takes its units out of `quantityReady`
    // — but ONLY an ad-hoc one raised outside the QC flow (no
    // `packingSlipID`). Issues created during QC carry a `packingSlipID`, and
    // the QC page already excludes their units from the packing slip's `ready`
    // count (`ready = min(ready, included - undeliverable)` in
    // QualityControl.vue), so `quantityReady` never counted them in the first
    // place. Decrementing again would double-count the shortage (sale
    // 01000031A line 1 dropped to quantityReady=2 when QC had passed 3). This
    // mirrors the backend guard in functions/issues.js `updateCriticalDamage`,
    // which only adjusts quantities for issues where `!packingSlipID`.
    await applyIssueHoldTransition(dispatch, state, {
      projectID,
      saleID,
      before: null,
      after: issue.data,
    });
  } else {
    // Existing Issue
    issue.data.modified = new Date();
    issue.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_ISSUE", {
      projectID,
      saleID,
      issueID: issue.id,
    });

    await saveDocuments(current.data);
    batch.set(doc(db, issue.path).withConverter(Issue), issue);
    await updateSale();
    await batch.commit();

    // Re-apply the hold for whatever changed: "requires a new order" toggled,
    // the quantity edited, the issue moved to another item, or the status
    // flipped between open and resolved (see `issueHoldsUnits`). Ad-hoc
    // issues only — QC-originated issues already have their units excluded
    // from `quantityReady` via the QC page's `ready` cap, so adjusting them
    // would double-count (see the creation-path note above and the backend
    // `updateCriticalDamage` `!packingSlipID` guard).
    if (current) {
      await applyIssueHoldTransition(dispatch, state, {
        projectID,
        saleID,
        before: current.data,
        after: issue.data,
      });
    }
  }
  return issue;
};

export const UPDATE_ISSUE = async (
  { dispatch, state },
  { projectID, saleID, issueID, changes }
) => {
  // The pre-write state decides the hold transition below. The store copy is
  // what the callers (the order dialog's "Resolve sale issues" toggle, receiving)
  // are looking at, so it is the right "before".
  const current = await dispatch("GET_ISSUE", { projectID, saleID, issueID });
  const before = current ? current.data : null;

  const updateSale = async () => {
    let saleChanges = {};
    forEach(
      pick(changes, ["itemID", "isCritical", "isResolved"]),
      (value, key) => {
        saleChanges[`status.issues.${issueID}.${key}`] = value;
      }
    );
    if (!isEmpty(saleChanges)) {
      return dispatch("UPDATE_SALE_STATUS", {
        projectID,
        saleID,
        changes: saleChanges,
      });
    }
  };

  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  await updateDoc(doc(db, Issue.getPath(projectID, saleID, issueID)), changes);
  await updateSale();

  if (before) {
    await applyIssueHoldTransition(dispatch, state, {
      projectID,
      saleID,
      before,
      after: { ...before, ...changes },
    });
  }
};

export const DELETE_ISSUE = async (
  { dispatch, state },
  { projectID, saleID, issueID }
) => {
  const issue = await dispatch("GET_ISSUE", { projectID, saleID, issueID });

  const batch = writeBatch(db);
  batch.delete(doc(db, Issue.getPath(projectID, saleID, issueID)));
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes: {
      [`status.issues.${issueID}`]: deleteField(),
    },
  });
  await batch.commit();

  // Deleting an issue that still HOLDS units hands them back. A resolved issue
  // already did that when it was closed, and a QC-originated issue never took
  // any (its units were already excluded by the QC `ready` cap), so neither
  // may re-add — see `issueHoldsUnits` and the backend `updateCriticalDamage`.
  if (issue) {
    await applyIssueHoldTransition(dispatch, state, {
      projectID,
      saleID,
      before: issue.data,
      after: null,
    });
  }
};
